import AsyncStorage from '@react-native-async-storage/async-storage';

// Storage + helpers for restoring where the user left off after the OS
// terminates the app in the background. Kept framework-agnostic (no React /
// expo-router imports) so both the restore hook and the store (logout) can use
// it without circular dependencies.

export const LAST_ROUTE_STORAGE_KEY = '@gabai/lastAppRoute/v1';
export const LAST_CHAT_SCROLL_STORAGE_KEY = '@gabai/lastChatScroll/v1';

// Don't resurrect very old state — after this long a fresh home screen is the
// more expected experience. Generous enough to cover same-day returns and
// multi-day-running conversations.
export const RESTORE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const HOME_ROUTES = new Set(['/app', '/']);

// Auth/checkout flow routes must never be saved or restored. Restoring onto
// the paywall created a launch loop for subscribers: restore → /paywall →
// "already Plus" redirect → home → fresh mount restores /paywall again,
// forever. (readLastRoute also validates with isRestorableRoute, so shipping
// this filter self-heals devices that already have /paywall persisted.)
const NON_RESTORABLE_PREFIXES = [
  '/paywall',
  '/login', // also covers /login-authorize
  '/signup',
  '/onboarding',
  '/reset-password',
  // Shell routes that immediately open a modal or Redirect elsewhere on
  // native. Restoring onto them causes a cold-start bounce (and used to
  // contribute to launch thrash when combined with other redirects).
  '/settings',
  '/deck', // covers /deck and /decks/recipes*
  '/modal-test',
];

// Container routes worth rebuilding a back stack for, so OS back / swipe from a
// restored chat returns to where it was opened (e.g. the collection).
const PARENT_ROUTE_PREFIXES = ['/collections/', '/tags/'];
// Routes that can sensibly sit on top of a parent container.
const CHILD_ROUTE_PREFIXES = ['/c/', '/g/', '/m/'];

export function isHomeRoute(pathname) {
  return !!pathname && HOME_ROUTES.has(pathname);
}

export function isRestorableRoute(pathname) {
  if (!pathname) return false;
  if (HOME_ROUTES.has(pathname)) return false;
  // /coworker is a redirect onto the home Work composer — restoring it
  // just bounces through a blank shell on cold start.
  if (pathname === '/coworker' || pathname === '/coworker/') return false;
  if (NON_RESTORABLE_PREFIXES.some((p) => pathname.startsWith(p))) return false;
  return true;
}

export function isParentRoute(pathname) {
  if (!pathname) return false;
  return PARENT_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
}

export function isChildRoute(pathname) {
  if (!pathname) return false;
  return CHILD_ROUTE_PREFIXES.some((p) => pathname.startsWith(p));
}

// Pull the conversation id out of a chat route (`/c/<id>`), else null.
export function conversationIdFromPath(pathname) {
  if (!pathname) return null;
  const match = pathname.match(/^\/c\/([^/?#]+)/);
  return match ? match[1] : null;
}

export async function saveLastRoute(record) {
  try {
    if (!record || !isRestorableRoute(record.path)) {
      await AsyncStorage.removeItem(LAST_ROUTE_STORAGE_KEY);
      return;
    }
    await AsyncStorage.setItem(
      LAST_ROUTE_STORAGE_KEY,
      JSON.stringify({ ...record, ts: Date.now() }),
    );
  } catch (_) {
    // best-effort; never let route persistence break navigation
  }
}

export async function readLastRoute() {
  try {
    const raw = await AsyncStorage.getItem(LAST_ROUTE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !isRestorableRoute(parsed.path)) return null;
    if (typeof parsed.ts !== 'number' || Date.now() - parsed.ts >= RESTORE_TTL_MS) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

// Scroll memory: we only remember the single most-recently-active chat's
// position (the one we'd restore anyway), keyed by conversation id, so storage
// stays bounded. `anchorMessageId` is the id of the top-most visible message —
// restoring by message id reuses the chat's robust scroll-to-message flow,
// which already tolerates async layout growth and archived-message pagination.
export async function saveLastChatScroll(conversationId, anchorMessageId) {
  try {
    if (!conversationId || !anchorMessageId) {
      await AsyncStorage.removeItem(LAST_CHAT_SCROLL_STORAGE_KEY);
      return;
    }
    await AsyncStorage.setItem(
      LAST_CHAT_SCROLL_STORAGE_KEY,
      JSON.stringify({ conversationId, anchorMessageId, ts: Date.now() }),
    );
  } catch (_) {
    // best-effort
  }
}

// When the user is back at the bottom of a chat, drop any saved anchor so the
// next restore lands at the natural bottom instead of a stale position.
export async function clearLastChatScroll() {
  try {
    await AsyncStorage.removeItem(LAST_CHAT_SCROLL_STORAGE_KEY);
  } catch (_) {
    // best-effort
  }
}

export async function readLastChatScroll(conversationId) {
  try {
    const raw = await AsyncStorage.getItem(LAST_CHAT_SCROLL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.anchorMessageId) return null;
    if (conversationId && parsed.conversationId !== conversationId) return null;
    if (typeof parsed.ts !== 'number' || Date.now() - parsed.ts >= RESTORE_TTL_MS) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

export async function clearLastRoute() {
  try {
    await Promise.all([
      AsyncStorage.removeItem(LAST_ROUTE_STORAGE_KEY),
      AsyncStorage.removeItem(LAST_CHAT_SCROLL_STORAGE_KEY),
    ]);
  } catch (_) {
    // best-effort
  }
}
